import LangEnum from '../../lang/LangEnum';
import { Option } from '../../lib/OptionsLib';
import { BusinessPolicyType } from '../../services/modules/business-policies/businessPolicies.types';
import { Role } from '../orders/actions/types/actions.types';
import { OrderType } from '../orders/interfaces/orderType.enum';
import { SynchronizedType } from '../orders/order.types';
import { BusinessPolicyCriteriaType } from './enums/business-policy-criteria-type.enum';
import { BusinessPolicyDiscountType } from './enums/business-policy-discount-type.enum';
import { BusinessPolicyLogicalOperatorType } from './enums/business-policy-logical-operator-type.enum';
import { BusinessPolicyNumberOperator } from './enums/business-policy-number-operator.enum';
import { BusinessPolicySetOperator } from './enums/business-policy-set-operator.enum';
import { CurrentOrderHandlingEnum } from './enums/current-order-handling.enum';
import { DiscountApplicationRuleEnum } from './enums/discount-application-rule.enum';
import { DiscountApplicationTypeEnum } from './enums/discount-application-type.enum';

export interface LangEnumOption extends Option {
  label: LangEnum;
  tooltipContent?: LangEnum;
}

export const businessPolicyTypeTranslator: {
  [key in BusinessPolicyType]: LangEnum;
} = {
  [BusinessPolicyType.ORDER_ITEM_DISCOUNT]: LangEnum.BUSINESS_POLICY_ORDER_ITEM_DISCOUNT,
  [BusinessPolicyType.ORDER_PRODUCTS_RESTRICTION]:
    LangEnum.BUSINESS_POLICY_ORDER_PRODUCTS_RESTRICTION,
  [BusinessPolicyType.ORDER_RESTRICTION]: LangEnum.BUSINESS_POLICY_ORDER_RESTRICTION,
  [BusinessPolicyType.ORDER_WARNING]: LangEnum.BUSINESS_POLICY_ORDER_WARNING,
  [BusinessPolicyType.ORDER_VOUCHER]: LangEnum.GLOBAL_VOUCHER,
  [BusinessPolicyType.CASHBACK]: LangEnum.GLOBAL_CASHBACK,
  [BusinessPolicyType.RELEASE_PAYMENT_METHODS]: LangEnum.BUSINESS_POLICY_RELEASE_PAYMENT_METHODS,
};

const BUSINESS_POLICY_TYPE_ORDER: BusinessPolicyType[] = [
  BusinessPolicyType.ORDER_ITEM_DISCOUNT,
  BusinessPolicyType.ORDER_PRODUCTS_RESTRICTION,
  BusinessPolicyType.ORDER_RESTRICTION,
  BusinessPolicyType.ORDER_WARNING,
  BusinessPolicyType.RELEASE_PAYMENT_METHODS,
  BusinessPolicyType.ORDER_VOUCHER,
  BusinessPolicyType.CASHBACK,
];

export const businessPolicyTypeOptions: Array<LangEnumOption & { value: BusinessPolicyType }> =
  BUSINESS_POLICY_TYPE_ORDER.map((type) => ({
    value: type,
    label: businessPolicyTypeTranslator[type],
  }));

export const businessPolicyCashbackDiscountTypeOptions: LangEnumOption[] = [
  {
    value: BusinessPolicyDiscountType.FIXED_VALUE_FROM_PERCENTAGE,
    label: LangEnum.BUSINESS_POLICY_DISCOUNT_TYPE_RULE_FIXED_VALUE_FROM_PERCENTAGE_LABEL,
  },
  {
    value: BusinessPolicyDiscountType.VALUE,
    label: LangEnum.GLOBAL_FIXED_VALUE,
  },
];

export const businessPolicyDiscountTypeOptions: LangEnumOption[] = [
  {
    value: BusinessPolicyDiscountType.PERCENTAGE,
    label: LangEnum.COUPONS_FORM_TYPE_PERCENT,
    tooltipContent: LangEnum.BUSINESS_POLICY_VOUCHER_ORDER_CONDITION_PERCENTAGE_RULE,
  },
  {
    value: BusinessPolicyDiscountType.VALUE,
    label: LangEnum.GLOBAL_FIXED_VALUE,
    tooltipContent: LangEnum.BUSINESS_POLICY_VOUCHER_ORDER_CONDITION_FIXED_VALUE_RULE,
  },
];

export const businessPolicyOrderItemDiscountTypeOptions: LangEnumOption[] = [
  {
    value: BusinessPolicyDiscountType.PERCENTAGE,
    label: LangEnum.COUPONS_FORM_TYPE_PERCENT,
  },
  {
    value: BusinessPolicyDiscountType.VALUE,
    label: LangEnum.GLOBAL_FIXED_VALUE,
  },
];

export const businessPolicyDiscountApplicationRuleOptions: LangEnumOption[] = [
  {
    value: DiscountApplicationRuleEnum.APPLY_ON_GROSS_VALUE,
    label: LangEnum.BUSINESS_POLICIES_DISCOUNT_APPLY_ON_GROSS_VALUE_RULE,
    tooltipContent: LangEnum.BUSINESS_POLICIES_DISCOUNT_APPLY_ON_GROSS_VALUE_RULE_DESCRIPTION,
  },
  {
    value: DiscountApplicationRuleEnum.APPLY_ON_PROMOTIONAL_PRICE,
    label: LangEnum.BUSINESS_POLICIES_DISCOUNT_APPLY_ON_PROMOTIONAL_PRICE_RULE,
    tooltipContent: LangEnum.BUSINESS_POLICIES_DISCOUNT_APPLY_ON_PROMOTIONAL_PRICE_RULE_DESCRIPTION,
  },
];

export const businessPolicyDiscountApplicationTypeOptions: LangEnumOption[] = [
  {
    value: DiscountApplicationTypeEnum.COMPETITIVE,
    label: LangEnum.BUSINESS_POLICIES_DISCOUNT_APPLICATION_COMPETITIVE_TYPE,
    tooltipContent: LangEnum.BUSINESS_POLICIES_DISCOUNT_APPLICATION_COMPETITIVE_TYPE_DESCRIPTION,
  },
  {
    value: DiscountApplicationTypeEnum.CUMULATIVE,
    label: LangEnum.BUSINESS_POLICIES_DISCOUNT_APPLICATION_CUMULATIVE_TYPE,
    tooltipContent: LangEnum.BUSINESS_POLICIES_DISCOUNT_APPLICATION_CUMULATIVE_TYPE_DESCRIPTION,
  },
];

export const businessPolicyRolesOptions: Array<LangEnumOption & { value: Role }> = [
  {
    value: Role.ADMIN_LOJA,
    label: LangEnum.USER_ROLES_STORE_ADMIN,
  },
  {
    value: Role.COMPRADOR,
    label: LangEnum.GLOBAL_BUYER,
  },
  {
    value: Role.VENDEDOR,
    label: LangEnum.GLOBAL_SELLER,
  },
  {
    value: Role.ADMIN_MARCA,
    label: LangEnum.USER_ROLES_BRAND_ADMIN,
  },
];

export const businessPolicyNumberOperatorOptions: Array<
  LangEnumOption & { value: BusinessPolicyNumberOperator }
> = [
  {
    value: BusinessPolicyNumberOperator.BIGGER_THAN_OR_EQUAL_TO,
    label: LangEnum.BUSINESS_POLICY_OPERATOR_BIGGER_THAN_OR_EQUAL_LABEL,
  },
  {
    value: BusinessPolicyNumberOperator.LESS_THAN_OR_EQUAL_TO,
    label: LangEnum.BUSINESS_POLICY_OPERATOR_LESS_THAN_OR_EQUAL_LABEL,
  },
  {
    value: BusinessPolicyNumberOperator.LESS_THAN,
    label: LangEnum.BUSINESS_POLICY_OPERATOR_LESS_THAN_LABEL,
  },
  {
    value: BusinessPolicyNumberOperator.BIGGER_THAN,
    label: LangEnum.BUSINESS_POLICY_OPERATOR_BIGGER_THAN_LABEL,
  },
  {
    value: BusinessPolicyNumberOperator.DIFFERENT_THAN,
    label: LangEnum.BUSINESS_POLICY_OPERATOR_DIFFERENT_THAN_LABEL,
  },
  {
    value: BusinessPolicyNumberOperator.EQUAL_TO,
    label: LangEnum.BUSINESS_POLICY_OPERATOR_EQUAL_TO_LABEL,
  },
];

export const businessPolicyNumberOperatorSymbol: {
  [key in BusinessPolicyNumberOperator]: string;
} = {
  [BusinessPolicyNumberOperator.BIGGER_THAN_OR_EQUAL_TO]: '≥',
  [BusinessPolicyNumberOperator.LESS_THAN_OR_EQUAL_TO]: '≤',
  [BusinessPolicyNumberOperator.BIGGER_THAN]: '>',
  [BusinessPolicyNumberOperator.LESS_THAN]: '<',
  [BusinessPolicyNumberOperator.EQUAL_TO]: '=',
  [BusinessPolicyNumberOperator.DIFFERENT_THAN]: '≠',
};

export const businessPolicySetOperatorOptions: Array<
  LangEnumOption & { value: BusinessPolicySetOperator }
> = [
  {
    value: BusinessPolicySetOperator.IN,
    label: LangEnum.BUSINESS_POLICY_OPERATOR_IN_LABEL,
  },
  {
    value: BusinessPolicySetOperator.NOT_IN,
    label: LangEnum.BUSINESS_POLICY_OPERATOR_NOT_IN_LABEL,
  },
];

export const businessPolicyLogicalOperatorOptions: LangEnumOption[] = [
  {
    value: BusinessPolicyLogicalOperatorType.OR,
    label: LangEnum.BUSINESS_POLICY_OPERATOR_OR_LABEL,
  },
  {
    value: BusinessPolicyLogicalOperatorType.AND,
    label: LangEnum.BUSINESS_POLICY_OPERATOR_AND_LABEL,
  },
];

export const businessPolicySetCurrentOrderHandlingOptions: Array<
  LangEnumOption & { value: CurrentOrderHandlingEnum }
> = [
  {
    value: CurrentOrderHandlingEnum.CONSIDER_CURRENT,
    label: LangEnum.BUSINESS_POLICY_CURRENT_ORDER_HANDLING_CONSIDER_CURRENT_LABEL,
  },
  {
    value: CurrentOrderHandlingEnum.IGNORE_CURRENT,
    label: LangEnum.BUSINESS_POLICY_CURRENT_ORDER_HANDLING_IGNORE_CURRENT_LABEL,
  },
  {
    value: CurrentOrderHandlingEnum.IGNORE_IF_LINKED_TO_SUGGESTED,
    label: LangEnum.BUSINESS_POLICY_CURRENT_ORDER_HANDLING_IGNORE_IF_LINKED_TO_SUGGESTED_LABEL,
  },
];

export const businessPolicySetSynchronizedStatusOptions: LangEnumOption[] = [
  {
    value: SynchronizedType.SUCCESS,
    label: LangEnum.GLOBAL_SUCCESS,
  },
  {
    value: SynchronizedType.ERROR,
    label: LangEnum.GLOBAL_ERROR,
  },
  {
    value: SynchronizedType.NOT_SYNCHRONIZED,
    label: LangEnum.GLOBAL_INTEGRATION_NOT_SYNCHRONIZED,
  },
];

export const orderTypeOptions: Array<LangEnumOption & { value: OrderType }> = [
  {
    value: OrderType.PRE_ORDER,
    label: LangEnum.GLOBAL_PRE_ORDER,
  },
  {
    value: OrderType.READY_FOR_DELIVERY,
    label: LangEnum.GLOBAL_READY_FOR_DELIVERY,
  },
  {
    value: OrderType.BOTH,
    label: LangEnum.GLOBAL_BOTH,
  },
];

export const shortSetOperatorLangEnum: { [key in BusinessPolicySetOperator]: LangEnum } = {
  [BusinessPolicySetOperator.IN]: LangEnum.GLOBAL_IS,
  [BusinessPolicySetOperator.NOT_IN]: LangEnum.GLOBAL_IS_NOT,
};

export const criteriaLangEnum: { [key in BusinessPolicyCriteriaType]: LangEnum } = {
  [BusinessPolicyCriteriaType.ORDER_ITEM_GROSS_VALUE_SUM]: LangEnum.GLOBAL_ITEM_GROSS_VALUE,
  [BusinessPolicyCriteriaType.ORDER_ITEM_UNIT_PRICE_SUM]:
    LangEnum.BUSINESS_POLICY_ORDER_ITEM_UNIT_PRICE_SUM_LABEL,
  [BusinessPolicyCriteriaType.ORDER_NET_VALUE]: LangEnum.BUSINESS_POLICY_ORDER_NET_VALUE_LABEL,
  [BusinessPolicyCriteriaType.ORDER_TYPES]: LangEnum.GLOBAL_AVAILABILITY,
  [BusinessPolicyCriteriaType.PIECES]: LangEnum.BUSINESS_POLICY_PIECES_LABEL,
  [BusinessPolicyCriteriaType.PIECES_BY_PRODUCT_COLOR]:
    LangEnum.BUSINESS_POLICY_PIECES_BY_PRODUCT_COLOR_LABEL,
  [BusinessPolicyCriteriaType.PIECES_BY_ORDER_PRODUCT]:
    LangEnum.BUSINESS_POLICY_PIECES_BY_ORDER_PRODUCT_LABEL,
  [BusinessPolicyCriteriaType.PIECES_BY_SUGGESTED_ORDER_PRODUCT]:
    LangEnum.BUSINESS_POLICY_PIECES_BY_SUGGESTED_ORDER_PRODUCT_LABEL,
  [BusinessPolicyCriteriaType.AVAILABILITIES]: LangEnum.GLOBAL_AVAILABILITY,
  [BusinessPolicyCriteriaType.ORDER_CREATION_DATE]: LangEnum.GLOBAL_ORDER_CREATION_DATE,
  [BusinessPolicyCriteriaType.CLASSIFICATIONS]: LangEnum.GLOBAL_CLASSIFICATIONS,
  [BusinessPolicyCriteriaType.PRODUCT_COLORS]: LangEnum.GLOBAL_PRODUCT_COLOR,
  [BusinessPolicyCriteriaType.COMPARATIVE]: LangEnum.GLOBAL_NONE,
  [BusinessPolicyCriteriaType.ORDER_COLLECTION]: LangEnum.GLOBAL_ORDER_COLLECTIONS,
  [BusinessPolicyCriteriaType.ORDER_HISTORY]: LangEnum.ORDER_STATUS_LABEL,
  [BusinessPolicyCriteriaType.CUSTOMER_GROUP_CREDIT_LIMIT]: LangEnum.GLOBAL_CREDIT_LIMIT,
  [BusinessPolicyCriteriaType.CUSTOMER_GROUP_AVAILABLE_BALANCE]: LangEnum.GLOBAL_AVAILABLE_BALANCE,
  [BusinessPolicyCriteriaType.VOUCHER_VALUE]: LangEnum.BUSINESS_POLICY_ITEM_VOUCHER_VALUE_LABEL,
  [BusinessPolicyCriteriaType.DELIVERY_WINDOW]: LangEnum.GLOBAL_DELIVERY_WINDOW,
};
